import { FC } from 'react'
import { useParams } from 'react-router-dom'

import { IndicatorsList, Widget } from '@factorialco/f0-react/dist/experimental'
import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import {
  ContactInvoiceWidgetDocument,
  ContactInvoiceWidgetQuery,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

const prepareData = (data: ContactInvoiceWidgetQuery) => {
  const authorizedCount = data.finance?.authorized?.aggregate?.count ?? 0
  const outstandingCount = data.finance?.outstanding?.aggregate?.count ?? 0
  const overdueCount = data.finance?.overdue?.aggregate?.count ?? 0

  return {
    title: i18n.t('finance.contacts.contact_details.widgets.invoices.title'),
    authorized: {
      label: i18n.t('finance.contacts.contact_details.widgets.invoices.authorized'),
      content: authorizedCount.toString(),
    },
    outstanding: {
      label: i18n.t('finance.contacts.contact_details.widgets.invoices.outstanding'),
      content: outstandingCount.toString(),
    },
    overdue: {
      label: i18n.t('finance.contacts.contact_details.widgets.invoices.overdue'),
      content: overdueCount.toString(),
    },
  }
}

type PreparedData = ReturnType<typeof prepareData>

const buildInvoiceWidgetComponent = (contactType: FinanceValueObjectsContactTypeEnum) => {
  const InvoiceWidgetComponentWithContactType: FC<{ data: PreparedData }> = ({
    data: { title, authorized, outstanding, overdue },
  }) => {
    const { navigation } = useNavigation()
    const { contactId } = useParams()

    const destinationUrl =
      contactType === 'client'
        ? navigation.finance.sales.clients.contactId.fullPath({ contactId: String(contactId) })
        : navigation.finance.spending.vendors.contactId.fullPath({ contactId: String(contactId) })

    const link = {
      url: destinationUrl,
      title: title,
    }

    return (
      <Widget
        header={{
          title,
          link,
        }}
      >
        <IndicatorsList
          items={[
            {
              ...authorized,
            },
            {
              ...outstanding,
            },
            {
              ...overdue,
            },
          ]}
        />
      </Widget>
    )
  }

  return InvoiceWidgetComponentWithContactType
}

export const buildContactsInvoicesWidget = (contactType: FinanceValueObjectsContactTypeEnum) =>
  buildWidget({
    id: 'contactsInvoices',
    document: ContactInvoiceWidgetDocument,
    prepareData,
    component: buildInvoiceWidgetComponent(contactType),
  })
